import React, { useState } from 'react';
import { useDisputes } from '../hooks/useDisputes';

interface DisputeButtonProps {
  marketId:  number;
  disabled?: boolean;
  onSuccess?: () => void;
}

/**
 * DisputeButton
 *
 * Renders a button that allows a registered oracle to raise a dispute
 * against a settled market via the PredictionMarketMultiOracle contract.
 *
 * Shows a confirmation dialog before submitting to avoid accidental disputes.
 */
export default function DisputeButton({ marketId, disabled, onSuccess }: DisputeButtonProps) {
  const { raiseDispute, txStatus, txError, txHash, resetTx, isConfigured } = useDisputes();
  const [confirming, setConfirming] = useState(false);

  if (!isConfigured) {
    return (
      <span className="badge badge-expired" title="Multi-oracle contract not configured">
        Disputes unavailable
      </span>
    );
  }

  async function handleConfirm() {
    setConfirming(false);
    const ok = await raiseDispute(marketId);
    if (ok) onSuccess?.();
  }

  if (txStatus === 'success') {
    return (
      <div className="dispute-result dispute-result-success">
        ✅ Dispute raised
        {txHash && (
          <a
            href={`https://sepolia.etherscan.io/tx/${txHash}`}
            target="_blank"
            rel="noopener noreferrer"
            className="tx-toast-link"
            style={{ marginLeft: 8 }}
          >
            Tx ↗
          </a>
        )}
        <button className="btn-outline" style={{ marginLeft: 8 }} onClick={resetTx}>
          Dismiss
        </button>
      </div>
    );
  }

  if (txStatus === 'error') {
    return (
      <div className="dispute-result dispute-result-error">
        ❌ {txError ?? 'Dispute failed'}
        <button className="btn-outline" style={{ marginLeft: 8 }} onClick={resetTx}>
          Retry
        </button>
      </div>
    );
  }

  if (confirming) {
    return (
      <div className="dispute-confirm">
        <span>Raise dispute on Market #{marketId}?</span>
        <button
          className="btn-danger"
          style={{ marginLeft: 8 }}
          onClick={handleConfirm}
          disabled={txStatus === 'pending'}
        >
          {txStatus === 'pending' ? '⏳ Submitting…' : 'Confirm Dispute'}
        </button>
        <button
          className="btn-outline"
          style={{ marginLeft: 4 }}
          onClick={() => setConfirming(false)}
          disabled={txStatus === 'pending'}
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <button
      className="btn-outline btn-danger-outline"
      onClick={() => setConfirming(true)}
      disabled={disabled || txStatus === 'pending'}
      title="Raise a dispute against this market's settlement"
    >
      ⚠️ Raise Dispute
    </button>
  );
}
